/**
 * In-app task panel — a right-anchored slide-over that shows the task board without leaving the current
 * screen (the /tasks page remains the full view). Inbox / Outbox for everyone; the whole-org board for GM.
 * Reuses the shared TaskCard, and shares query keys with /tasks so creating/deleting anywhere refreshes it.
 * This is the in-app half of "the agent = Both" (Claude Code is the other half, via the bearer API).
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { getTaskInbox, getTaskOutbox, getAllTasks, type TaskContext } from '../lib/api';
import { currentViewTab, currentPageUrl, friendlyRouteLabel } from '../lib/captureContext';
import { useTaskConfigured, TASK_DISABLED_TITLE } from '../lib/useTaskConfigured';
import TaskCard from './TaskCard';
import DelegateTaskModal from './DelegateTaskModal';

type Box = 'inbox' | 'outbox' | 'all';

export default function TaskPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const loc = useLocation();
  const { configured, role } = useTaskConfigured();
  const isGm = role === 'gm';
  const [box, setBox] = useState<Box>('inbox');
  const [showNew, setShowNew] = useState(false);

  const inbox = useQuery({ queryKey: ['tasks', 'inbox'], queryFn: getTaskInbox, enabled: open });
  const outbox = useQuery({ queryKey: ['tasks', 'outbox'], queryFn: getTaskOutbox, enabled: open });
  const all = useQuery({ queryKey: ['tasks', 'all'], queryFn: getAllTasks, enabled: open && box === 'all' && isGm });
  const refresh = () => qc.invalidateQueries({ queryKey: ['tasks'] });

  if (!open) return null;
  const q = box === 'inbox' ? inbox : box === 'outbox' ? outbox : all;
  const list = q.data ?? [];
  const tabBtn = (b: Box, label: string, n: number) => (
    <button className={`tab${box === b ? ' active' : ''}`} onClick={() => setBox(b)} style={{ fontSize: 13 }}>{label} ({n})</button>
  );
  const newCtx: TaskContext = { kind: 'screen', label: friendlyRouteLabel(loc), view_tab: currentViewTab(loc) ?? undefined };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} className="drawer"
        style={{ position: 'fixed', top: 0, right: 0, height: '100vh', width: 'min(460px, 94vw)', background: 'var(--card,#fff)', color: 'var(--ink)', boxShadow: '-8px 0 32px rgba(0,0,0,0.25)', overflowY: 'auto', padding: 16, boxSizing: 'border-box' }}>
        <div className="row-wrap" style={{ alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <h3 style={{ margin: 0, marginRight: 'auto' }}>🗂 任务面板 / Task board</h3>
          <button onClick={onClose} title="关闭 / close">✕</button>
        </div>

        <div className="row-wrap" style={{ gap: 6, marginBottom: 10 }}>
          {tabBtn('inbox', '📥 收件箱 / Inbox', inbox.data?.length ?? 0)}
          {tabBtn('outbox', '📤 已派发 / Outbox', outbox.data?.length ?? 0)}
          {isGm && tabBtn('all', '🗂 全部 / All', all.data?.length ?? 0)}
        </div>

        <button className="btn-primary" style={{ marginBottom: 10 }} disabled={!configured}
          title={configured ? '派发新任务 / Delegate a new task' : TASK_DISABLED_TITLE}
          onClick={() => configured && setShowNew(true)}>
          📋 派发新任务 / New task
        </button>

        {q.isLoading && <div className="muted small">加载中… / loading…</div>}
        {!q.isLoading && list.length === 0 && <div className="card muted">暂无任务 / nothing here.</div>}
        {list.map((t) => <TaskCard key={t.id} t={t} box={box} onChanged={refresh} />)}

        <DelegateTaskModal
          open={showNew}
          onClose={() => setShowNew(false)}
          context={newCtx}
          pageUrl={currentPageUrl(loc)}
          viewTab={currentViewTab(loc)}
        />
      </div>
    </div>
  );
}
